// useTeamMutations — the teams' write surface (ADR-083): create · edit · disable /
// reactivate · delete while empty · the members in and out and whether each can be
// assigned · the work area. Each invalidates the `['teams', projectId]` prefix on
// success so the list, the page and the names refetch together. Errors bubble so the
// form shell or the dialog maps the backend `code` (TEAM_NAME_EXISTS · TEAM_DISABLED ·
// CHAINS_END · REFERENCE_IN_USE …) through errors.json; 401 is the apiClient's (ADR-003).

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { AddTeamMembersRequest, CreateTeamRequest, TeamWorkAreaRequest, UpdateTeamMemberRequest, UpdateTeamRequest } from '@ems-app/schemas/teams/requests';
import type { Team, TeamMembersResponse } from '@ems-app/schemas/teams/responses';
import { apiClient } from '../lib/apiClient.js';
import { chainsKeys } from './useChains.js';
import { teamsKeys } from './useTeams.js';
import { refreshOnSettled } from '../lib/settled.js';

function useInvalidateTeams(projectId: string): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: teamsKeys.all(projectId) });
}

export function useCreateTeam(projectId: string): UseMutationResult<Team, Error, CreateTeamRequest> {
  const invalidate = useInvalidateTeams(projectId);
  return useMutation({
    mutationFn: async (body) => (await apiClient.post<Team>(`/projects/${projectId}/teams`, body)).data,
    onSuccess: invalidate,
  });
}

export function useUpdateTeam(projectId: string): UseMutationResult<Team, Error, { teamId: string; body: UpdateTeamRequest }> {
  const invalidate = useInvalidateTeams(projectId);
  return useMutation({
    mutationFn: async ({ teamId, body }) => (await apiClient.patch<Team>(`/projects/${projectId}/teams/${teamId}`, body)).data,
    onSuccess: invalidate,
  });
}

/** POST …/teams/:id/{disable,reactivate} — the reversible pause (its chains stay, dormant). */
export function useSetTeamDisabled(projectId: string): UseMutationResult<Team, Error, { teamId: string; disabled: boolean }> {
  const invalidate = useInvalidateTeams(projectId);
  return useMutation({
    mutationFn: async ({ teamId, disabled }) => (await apiClient.post<Team>(`/projects/${projectId}/teams/${teamId}/${disabled ? 'disable' : 'reactivate'}`)).data,
    onSettled: refreshOnSettled(invalidate),
  });
}

/** DELETE …/teams/:id — only while empty and never used; a refusal (409 REFERENCE_IN_USE) names the uses. */
export function useDeleteTeam(projectId: string): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (teamId) => {
      await apiClient.delete(`/projects/${projectId}/teams/${teamId}`);
    },
    // The list and the names refetch — NOT the gone team's detail or chains: invalidating those would refetch, 404
    // (TEAM_NOT_FOUND) and trip the apiClient's not-found redirect before the page navigates back (the users' remove
    // precedent); they are dropped from the cache instead. A REFUSED delete leaves the team: its detail re-reads too.
    onSuccess: (_d, teamId) => {
      qc.removeQueries({ queryKey: teamsKeys.detail(projectId, teamId) });
      qc.removeQueries({ queryKey: chainsKeys.scope(projectId, 'team', teamId) });
    },
    onSettled: refreshOnSettled((_d, teamId, err) => {
      void qc.invalidateQueries({ queryKey: [...teamsKeys.all(projectId), 'list'] });
      void qc.invalidateQueries({ queryKey: [...teamsKeys.all(projectId), 'names'] });
      if (err) void qc.invalidateQueries({ queryKey: teamsKeys.detail(projectId, teamId) });
    }),
  });
}

export function useAddTeamMembers(projectId: string): UseMutationResult<TeamMembersResponse, Error, { teamId: string; body: AddTeamMembersRequest }> {
  const invalidate = useInvalidateTeams(projectId);
  return useMutation({
    mutationFn: async ({ teamId, body }) => (await apiClient.post<TeamMembersResponse>(`/projects/${projectId}/teams/${teamId}/members`, body)).data,
    onSuccess: invalidate,
  });
}

export function useUpdateTeamMember(projectId: string): UseMutationResult<TeamMembersResponse, Error, { teamId: string; userId: string; body: UpdateTeamMemberRequest }> {
  const invalidate = useInvalidateTeams(projectId);
  return useMutation({
    mutationFn: async ({ teamId, userId, body }) => (await apiClient.patch<TeamMembersResponse>(`/projects/${projectId}/teams/${teamId}/members/${userId}`, body)).data,
    onSettled: refreshOnSettled(invalidate),
  });
}

/** DELETE …/teams/:id/members/:userId — refused CHAINS_END while the person holds one of the team's responsibilities. */
export function useRemoveTeamMember(projectId: string): UseMutationResult<TeamMembersResponse, Error, { teamId: string; userId: string }> {
  const invalidate = useInvalidateTeams(projectId);
  return useMutation({
    mutationFn: async ({ teamId, userId }) => (await apiClient.delete<TeamMembersResponse>(`/projects/${projectId}/teams/${teamId}/members/${userId}`)).data,
    onSettled: refreshOnSettled(invalidate),
  });
}

export function useSetTeamWorkArea(projectId: string): UseMutationResult<void, Error, { teamId: string; body: TeamWorkAreaRequest }> {
  const invalidate = useInvalidateTeams(projectId);
  return useMutation({
    mutationFn: async ({ teamId, body }) => {
      await apiClient.put(`/projects/${projectId}/teams/${teamId}/work-area`, body);
    },
    onSuccess: invalidate,
  });
}
